import {
  partnerDescriptionKey,
  partnerNameKey,
  sectionDescriptionKey,
  sectionTitleKey,
  serviceDescriptionKey,
  serviceTitleKey,
  tagLabelKey,
} from "@/lib/marketplaceTranslations";

export const MARKETPLACE_ALL_CATEGORIES_VALUE = "__all";

const sortStrings = (values = []) => {
  const list = Array.isArray(values) ? values : [values];

  return list
    .filter((value) => value !== undefined && value !== null)
    .map(String)
    .sort((left, right) => left.localeCompare(right));
};

const trimToEmpty = (value) => String(value ?? "").trim();

export const normalizeMarketplaceFilters = (filters = {}) => ({
  search: trimToEmpty(filters.search),
  categoryId:
    trimToEmpty(filters.categoryId) === MARKETPLACE_ALL_CATEGORIES_VALUE
      ? ""
      : trimToEmpty(filters.categoryId),
  compatibleWithFacetIds: sortStrings(filters.compatibleWithFacetIds || []),
});

export const hasMarketplaceFilters = (filters = {}) => {
  const normalized = normalizeMarketplaceFilters(filters);

  return (
    normalized.search.length > 0 ||
    normalized.categoryId.length > 0 ||
    normalized.compatibleWithFacetIds.length > 0
  );
};

export const hasMarketplaceFacetFilters = (filters = {}) => {
  const normalized = normalizeMarketplaceFilters(filters);

  return (
    normalized.categoryId.length > 0 ||
    normalized.compatibleWithFacetIds.length > 0
  );
};

export const normalizeMarketplaceSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const matchesMarketplaceSearch = (search, fields = []) => {
  const tokens = normalizeMarketplaceSearchText(search)
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) return true;

  const haystack = normalizeMarketplaceSearchText(fields.filter(Boolean).join(" "));
  return tokens.every((token) => haystack.includes(token));
};

export const sortByMarketplaceOrder = (left, right) =>
  (left.sortOrder ?? 0) - (right.sortOrder ?? 0) ||
  String(left.key ?? "").localeCompare(String(right.key ?? ""));

export const getMarketplaceFacetGroups = (facets = []) => ({
  categoryFacets: facets.filter((facet) => facet.type === "Category"),
  compatibleFacets: facets.filter((facet) => facet.type === "CompatibleWith"),
});

export const buildMarketplaceSectionSearchFields = (t, sectionKey) => [
  t(sectionTitleKey(sectionKey)),
  t(sectionDescriptionKey(sectionKey)),
];

export const buildMarketplaceServiceSearchFields = (
  t,
  sectionKey,
  serviceKey,
  service,
) => [
  ...buildMarketplaceSectionSearchFields(t, sectionKey),
  t(serviceTitleKey(sectionKey, serviceKey)),
  t(serviceDescriptionKey(sectionKey, serviceKey)),
  ...(service.tags || []).map((tagKey) => t(tagLabelKey(tagKey))),
];

export const buildMarketplacePartnerSearchFields = (
  t,
  sectionKey,
  serviceKey,
  partner,
) => [
  t(partnerNameKey(sectionKey, serviceKey, partner.key)),
  t(partnerDescriptionKey(sectionKey, serviceKey, partner.key)),
];

export const partnerMatchesMarketplaceFacetFilters = (partner, filters = {}) => {
  const normalized = normalizeMarketplaceFilters(filters);

  if (
    normalized.categoryId &&
    partner.categoryFacetId !== normalized.categoryId
  ) {
    return false;
  }

  if (normalized.compatibleWithFacetIds.length > 0) {
    const partnerCompatible = new Set(partner.compatibleWithFacetIds || []);

    for (const facetId of normalized.compatibleWithFacetIds) {
      if (!partnerCompatible.has(facetId)) return false;
    }
  }

  return true;
};

export const partnerMatchesMarketplaceFilters = ({
  t,
  sectionKey,
  serviceKey,
  partner,
  filters = {},
  sectionMatches = false,
  serviceMatches = false,
}) => {
  const normalized = normalizeMarketplaceFilters(filters);
  const hasSearch = normalized.search.length > 0;
  const partnerMatches = hasSearch
    ? matchesMarketplaceSearch(
        normalized.search,
        buildMarketplacePartnerSearchFields(t, sectionKey, serviceKey, partner),
      )
    : false;

  const matchesContext =
    !hasSearch || sectionMatches || serviceMatches || partnerMatches;

  return partnerMatchesMarketplaceFacetFilters(partner, normalized) && matchesContext;
};
